import {EquipmentPlain} from '@hconnect/common'
import {typedPick, getSystemTimezone} from '@hconnect/common/utils'
import {omitBy, isNil} from 'lodash'
import moment, {Moment} from 'moment-timezone'

import {
  ProcessWorkOrderFormValuesEditDto,
  ProcessWorkOrderDto,
  ProcessWorkOrderAssignee,
  ProcessWorkOrderFormValues,
  ProcessWorkOrderEditDto,
  ProcessWorkOrderFormAttachmentFile,
  TaskStatus,
  ProcessWorkOrderRepetitionInfoDto,
  ProcessWorkOrderStatus
} from '../types'

export const mapToProcessWorkOrderFormValues = (
  data: ProcessWorkOrderDto | undefined,
  timezone: string = getSystemTimezone()
): ProcessWorkOrderFormValuesEditDto | undefined => {
  if (!data) {
    return undefined
  }
  const processWorkOrderEditFormValues: ProcessWorkOrderFormValuesEditDto = {
    ...typedPick(data, [
      'plantId',
      'title',
      'description',
      'mainEquipment',
      'processStage',
      'status',
      'attachments'
    ]),
    ...(data.equipment && {
      equipment: {
        equipment: data.equipment,
        ...(data.processStage && {processStage: {processStep: data.processStage}}),
        ...(data.mainEquipment && {mainEquipment: data.mainEquipment})
      } as EquipmentPlain
    }),
    dueDate: moment.tz(data.dueDate, timezone),
    ...(data.doNotStartBefore && {doNotStartBefore: moment.tz(data.doNotStartBefore, timezone)}),
    ...(data.assignees && {
      assignees: data.assignees.map((assignee: ProcessWorkOrderAssignee) => assignee.user)
    }),
    ...(data.repetitionInfo
      ? {isRecurring: true, repetitionInfo: data.repetitionInfo}
      : {isRecurring: false, repetitionInfo: undefined as never})
  }
  return processWorkOrderEditFormValues
}

export const mapToProcessWorkOrderEditDto = (
  submittedData: ProcessWorkOrderFormValues,
  originalData: ProcessWorkOrderDto,
  initialValues?: ProcessWorkOrderFormValuesEditDto
): ProcessWorkOrderEditDto => {
  const attachmentsToUpload: ProcessWorkOrderFormAttachmentFile[] = []
  const attachmentsToDelete: string[] = []

  if (submittedData.attachments?.length) {
    const existingNames = initialValues?.attachments?.map((a) => a.fileName) || []
    const newAttachments = submittedData.attachments.filter(
      (file) => !existingNames.includes(file.name)
    )

    attachmentsToUpload.push(
      ...newAttachments.map((file) => ({
        name: file.name,
        contentType: file.type,
        preview: URL.createObjectURL(file),
        isImage: file.type.includes('image'),
        originalFile: file
      }))
    )
  }

  if (initialValues?.attachments?.length) {
    const currentNames = submittedData.attachments?.map((f) => f.name) || []
    attachmentsToDelete.push(
      ...initialValues.attachments
        .filter((a) => !currentNames.includes(a.fileName))
        .map((a) => a.id?.toString() ?? '')
        .filter((id) => id !== '')
    )
  }

  const cleaned = omitBy(
    submittedData,
    (value) => isNil(value) || (Array.isArray(value) && value.length === 0)
  ) as Partial<ProcessWorkOrderFormValues>

  const toMoment = (value?: Moment | string | Date | null) => {
    if (!value) {
      return undefined
    }

    if (moment.isMoment(value)) {
      return value
    }

    if (value instanceof Date) {
      return moment(value)
    }

    return moment(value)
  }

  const normalizedDueDate = toMoment(cleaned.dueDate ?? submittedData.dueDate)
  const normalizedDoNotStartBefore = toMoment(
    cleaned.doNotStartBefore ?? submittedData.doNotStartBefore
  )

  const shouldIncludeDoNotStartBefore =
    !normalizedDueDate || !normalizedDoNotStartBefore
      ? true
      : !normalizedDueDate.isSame(normalizedDoNotStartBefore)

  return {
    ...cleaned,
    id: originalData.id,
    plantId: originalData.plantId,
    title: submittedData.title,
    description: submittedData.description,
    status: (cleaned.status ?? originalData.status) as
      | typeof cleaned.status
      | typeof originalData.status as
      | TaskStatus.Pending
      | TaskStatus.Ongoing
      | TaskStatus.Done
      | TaskStatus.Cancelled,
    dueDate: normalizedDueDate ?? moment(),
    doNotStartBefore: shouldIncludeDoNotStartBefore ? normalizedDoNotStartBefore : undefined,
    ...(cleaned.isRecurring
      ? {
          isRecurring: true,
          repetitionInfo: cleaned.repetitionInfo as ProcessWorkOrderRepetitionInfoDto
        }
      : {
          isRecurring: false as const,
          repetitionInfo: undefined as never
        }),
    attachments: attachmentsToUpload,
    attachmentsToDelete,
    equipment: submittedData.equipment?.equipment
  }
}

export const mapToSingleProcessWorkOrderStatusUpdateDto = (
  processWorkOrderDto: ProcessWorkOrderDto,
  newStatus: ProcessWorkOrderStatus
): ProcessWorkOrderEditDto => {
  return {
    id: processWorkOrderDto.id,
    plantId: processWorkOrderDto.plantId,
    title: processWorkOrderDto.title,
    description: processWorkOrderDto.description,
    equipment: processWorkOrderDto.equipment,
    mainEquipment: processWorkOrderDto.mainEquipment,
    processStage: processWorkOrderDto.processStage,
    dueDate: moment(processWorkOrderDto.dueDate),
    doNotStartBefore: processWorkOrderDto.doNotStartBefore
      ? moment(processWorkOrderDto.doNotStartBefore)
      : undefined,
    status: newStatus,
    attachments: [],
    isRecurring: false,
    repetitionInfo: undefined as never
  }
}
